import { ref, watch, Ref } from 'vue';

/**
 * "Error Details" for the error pages, held in memory only: a URL or a later
 * navigation can never show text here. axiosInstance.ts sets the details for
 * the error page it is about to show, and that page takes them once.
 *
 * The details are tagged with the route they are for, so the page being left
 * (still mounted when the interceptor sets them) does not take details meant
 * for the next one.
 */
const pending = ref<{ route: string; message: string | undefined } | null>(
  null
);

export const setErrorDetails = (route: string, message: string | undefined) => {
  pending.value = { route, message };
};

// From router.afterEach: details meant for another page are dropped, so a
// later plain navigation to that page shows none.
export const dropStaleErrorDetails = (routeName: unknown) => {
  if (pending.value && pending.value.route !== routeName) {
    pending.value = null;
  }
};

// For an error view: take the details meant for this page, including ones
// set while the page is already showing.
export const useErrorDetails = (routeName: string): Ref<string | undefined> => {
  const message = ref<string | undefined>();
  watch(
    pending,
    value => {
      if (value?.route === routeName) {
        message.value = value.message;
        pending.value = null;
      }
    },
    { immediate: true }
  );
  return message;
};
